/**
 * Pitch Deck Landing Page
 * 
 * This page provides an overview of the pitch deck generation feature
 * and shows users what to expect from the feature.
 */

import { Link } from "react-router-dom";
import { FileText, Upload, Sparkles, Download, Lightbulb, ArrowRight } from "lucide-react";
import { PitchDeckAccessGuard } from "@/components/pitch-deck/PitchDeckAccessGuard";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";

function PitchDeckContent() {
  return (
    <div className="p-6 md:p-8 max-w-4xl mx-auto">
      {/* Hero Section */}
      <div className="text-center mb-8">
        <h1 className="text-3xl md:text-4xl font-bold mb-4">
          📊 Pitch Deck Generator
        </h1>
        <p className="text-lg text-muted-foreground mb-6">
          Create your professional pitch deck in minutes.
        </p>
        <Button asChild size="lg" className="gap-2">
          <Link to="/pitch-deck/forms">
            Get Started
            <ArrowRight className="h-4 w-4" />
          </Link>
        </Button>
      </div>

      {/* Main Card */}
      <Card className="shadow-lg mb-8">
        <CardContent className="p-6 md:p-8">
          {/* Features Section */}
          <div className="bg-primary/5 border border-primary/20 rounded-lg p-6 mb-8">
            <h2 className="text-xl font-semibold mb-4 text-primary">
              What you'll create:
            </h2>
            <ul className="space-y-3">
              <li className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span>15-slide professional pitch deck</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span>Editable PowerPoint format (.pptx)</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span>Based on your business details</span>
              </li>
              <li className="flex items-center gap-3">
                <div className="h-2 w-2 rounded-full bg-primary" />
                <span>Download and customize offline</span>
              </li>
            </ul>
          </div>

          {/* How It Works Steps */}
          <div className="mb-8">
            <h2 className="text-xl font-semibold mb-4">How it works:</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Step 1 */}
              <div className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <FileText className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Step 1: Business Basics</h3>
                </div>
                <p className="text-sm text-muted-foreground ml-13">
                  Fill out Form 1 with your company info
                </p>
              </div>

              {/* Step 2 */}
              <div className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Upload className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Step 2: Market & Financials</h3>
                </div>
                <p className="text-sm text-muted-foreground ml-13">
                  Complete Form 2 with market data
                </p>
              </div>

              {/* Step 3 */}
              <div className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Sparkles className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Step 3: Generate</h3>
                </div>
                <p className="text-sm text-muted-foreground ml-13">
                  Click generate and wait ~5 minutes
                </p>
              </div>

              {/* Step 4 */}
              <div className="border border-border rounded-lg p-4 hover:border-primary/50 transition-colors">
                <div className="flex items-center gap-3 mb-2">
                  <div className="h-10 w-10 rounded-full bg-primary/10 flex items-center justify-center">
                    <Download className="h-5 w-5 text-primary" />
                  </div>
                  <h3 className="font-semibold">Step 4: Download</h3>
                </div>
                <p className="text-sm text-muted-foreground ml-13">
                  Get your .pptx file and customize
                </p>
              </div>
            </div>
          </div>

          {/* CTA */}
          <div className="text-center mb-8">
            <Button asChild size="lg" className="gap-2">
              <Link to="/pitch-deck/forms">
                Start Creating Your Pitch Deck
                <ArrowRight className="h-4 w-4" />
              </Link>
            </Button>
          </div>

          {/* Tip */}
          <div className="bg-muted rounded-lg p-4 flex items-start gap-3">
            <Lightbulb className="h-5 w-5 text-muted-foreground mt-0.5 flex-shrink-0" />
            <div>
              <p className="text-sm text-muted-foreground">
                <span className="font-medium">Tip:</span> Your progress is saved automatically 
                every 10 seconds. You can return anytime to continue where you left off.
              </p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}

export default function PitchDeck() {
  return (
    <PitchDeckAccessGuard>
      <PitchDeckContent />
    </PitchDeckAccessGuard>
  );
}
